import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Modal } from '@app/ui/modal';
import { Alert } from '@app/ui/alert';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import { RULE_DELETE_ONE } from '../gql';
import { Rule } from '../types';
import { getGraphQLErrorMessage } from '../utils';

export interface RuleDeleteDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly rule: Rule;
  readonly onDeleted: () => Promise<unknown> | void;
}

// Deletes the rule ROW from the draft version. Every published version holds its own copy of the
// rule, so nothing already published is touched — the warning below says exactly that, because
// "delete rule" reads as permanent and irreversible everywhere else in the product.
export const RuleDeleteDialog = ({ open, onClose, rule, onDeleted }: RuleDeleteDialogProps) => {
  const { t } = useTranslation('common');

  const [ruleDeleteOne, { loading: deleting }] = useMutation<{ ruleDeleteOne: boolean }>(RULE_DELETE_ONE);

  const handleDelete = async () => {
    try {
      await ruleDeleteOne({ variables: { input: { id: rule.id } } });
      toastSuccess(t('rules.delete.successMessage', { ruleId: rule.ruleId }));
      await onDeleted();
    } catch (e) {
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
    }
  };

  return (
    <Modal open={open} onClose={onClose} maxWidth="sm" title={t('rules.delete.dialogTitle', { ruleId: rule.ruleId })}>
      <Typography variant="body1">{rule.ruleText}</Typography>

      <Alert severity="warning" sx={{ mt: 2 }}>
        {t('rules.delete.draftOnlyWarning')}
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onClose}>{t('rules.delete.cancelButton')}</Button>
        <LoadingButton loading={deleting} variant="contained" color="error" onClick={handleDelete}>
          {t('rules.delete.confirmButton')}
        </LoadingButton>
      </Box>
    </Modal>
  );
};
